import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  DatePicker,
  Select,
  message,
  Modal,
  Descriptions,
  Statistic,
  Row,
  Col,
  Popconfirm,
  Switch,
  Form,
  Input,
  Tabs
} from 'antd';
import {
  ReloadOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  BarChartOutlined,
  SettingOutlined,
  EditOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const CronTaskLogs = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [filters, setFilters] = useState({
    taskType: undefined,
    status: undefined,
    dateRange: undefined
  });
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [stats, setStats] = useState([]);
  const [selectedTaskType, setSelectedTaskType] = useState('jindou_release');
  
  // 任务配置相关状态
  const [taskConfigs, setTaskConfigs] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form] = Form.useForm();

  const taskTypes = [
    { value: 'jindou_release', label: '金豆解锁' },
    { value: 'kyc_expired', label: 'KYC过期清理' }
  ];

  const statusMap = {
    running: { text: '执行中', color: 'processing' },
    success: { text: '成功', color: 'success' },
    failed: { text: '失败', color: 'error' }
  };

  useEffect(() => {
    fetchLogs();
    fetchTaskConfigs();
  }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        taskType: filters.taskType,
        status: filters.status
      };

      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.get('/admin/cron-tasks/logs', { params });
      setLogs(response.data.list);
      setTotal(response.data.total);
    } catch (error) {
      message.error('获取任务日志失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskConfigs = async () => {
    setConfigLoading(true);
    try {
      const response = await api.get('/admin/cron-tasks/configs');
      setTaskConfigs(response.data);
    } catch (error) {
      message.error('获取任务配置失败');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleRunTask = async (taskKey) => {
    const task = taskConfigs.find(t => t.task_key === taskKey);
    const taskName = task ? task.task_name : taskKey;

    Modal.confirm({
      title: '确认执行任务',
      content: `确定要立即执行【${taskName}】任务吗？`,
      onOk: async () => {
        try {
          const response = await api.post(`/admin/cron-tasks/run/${taskKey}`);
          message.success('任务执行成功');
          
          // 显示执行结果
          Modal.info({
            title: '任务执行结果',
            width: 600,
            content: (
              <pre style={{ maxHeight: 400, overflow: 'auto' }}>
                {JSON.stringify(response.data, null, 2)}
              </pre>
            )
          });
          
          fetchLogs();
          fetchTaskConfigs();
        } catch (error) {
          message.error('任务执行失败');
        }
      }
    });
  };

  const handleToggleTask = async (taskKey, isEnabled) => {
    try {
      await api.post(`/admin/cron-tasks/configs/${taskKey}/toggle`, {
        isEnabled
      });
      message.success(isEnabled ? '任务已启用' : '任务已禁用');
      fetchTaskConfigs();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    form.setFieldsValue({
      task_name: task.task_name,
      cron_expression: task.cron_expression,
      description: task.description
    });
    setEditModalVisible(true);
  };

  const handleSaveTask = async () => {
    try {
      const values = await form.validateFields();
      await api.put(`/admin/cron-tasks/configs/${editingTask.task_key}`, values);
      message.success('任务配置更新成功');
      setEditModalVisible(false);
      fetchTaskConfigs();
    } catch (error) {
      message.error('更新失败');
    }
  };

  const handleReloadAll = async () => {
    try {
      await api.post('/admin/cron-tasks/reload-all');
      message.success('所有任务已重新加载');
      fetchTaskConfigs();
    } catch (error) {
      message.error('重新加载失败');
    }
  };

  const handleCleanLogs = async () => {
    try {
      const response = await api.delete('/admin/cron-tasks/logs/clean', {
        params: { days: 30 }
      });
      message.success(`已清理 ${response.data.deletedCount} 条旧日志`);
      fetchLogs();
    } catch (error) {
      message.error('清理日志失败');
    }
  };

  const showStats = async (taskType) => {
    setSelectedTaskType(taskType);
    setStatsVisible(true);
    try {
      const response = await api.get(`/admin/cron-tasks/stats/${taskType}`, {
        params: { days: 7 }
      });
      setStats(response.data);
    } catch (error) {
      message.error('获取统计信息失败');
    }
  };

  const showDetail = (record) => {
    setSelectedLog(record);
    setDetailVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80
    },
    {
      title: '任务名称',
      dataIndex: 'task_name',
      width: 150
    },
    {
      title: '任务类型',
      dataIndex: 'task_type',
      width: 120,
      render: (type) => {
        const task = taskTypes.find(t => t.value === type);
        return task ? task.label : type;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status) => (
        <Tag color={statusMap[status]?.color}>
          {statusMap[status]?.text || status}
        </Tag>
      )
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      width: 180,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      width: 180,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '耗时(ms)',
      dataIndex: 'duration',
      width: 100,
      render: (duration) => duration ? Math.round(duration) : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => showDetail(record)}>
          详情
        </Button>
      )
    }
  ];

  const configColumns = [
    {
      title: '任务名称',
      dataIndex: 'task_name',
      width: 150
    },
    {
      title: '任务标识',
      dataIndex: 'task_key',
      width: 150
    },
    {
      title: 'Cron表达式',
      dataIndex: 'cron_expression',
      width: 120
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true
    },
    {
      title: '状态',
      dataIndex: 'is_enabled',
      width: 100,
      render: (enabled, record) => (
        <Switch
          checked={enabled === 1}
          onChange={(checked) => handleToggleTask(record.task_key, checked)}
        />
      )
    },
    {
      title: '运行状态',
      dataIndex: 'isRunning',
      width: 100,
      render: (isRunning) => (
        <Tag color={isRunning ? 'success' : 'default'}>
          {isRunning ? '运行中' : '已停止'}
        </Tag>
      )
    },
    {
      title: '执行次数',
      dataIndex: 'run_count',
      width: 100
    },
    {
      title: '最后执行',
      dataIndex: 'last_run_time',
      width: 180,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '下次执行',
      dataIndex: 'next_run_time',
      width: 180,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleRunTask(record.task_key)}
          >
            执行
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditTask(record)}
          >
            编辑
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="定时任务管理"
        extra={
          <Space>
            <Button
              icon={<BarChartOutlined />}
              onClick={() => showStats('jindou_release')}
            >
              统计
            </Button>
            <Popconfirm
              title="确定要清理30天前的旧日志吗？"
              onConfirm={handleCleanLogs}
            >
              <Button icon={<DeleteOutlined />}>清理旧日志</Button>
            </Popconfirm>
            <Popconfirm
              title="确定要重新加载所有任务吗？"
              onConfirm={handleReloadAll}
            >
              <Button icon={<ReloadOutlined />}>重新加载</Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchLogs();
                fetchTaskConfigs();
              }}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Tabs defaultActiveKey="configs">
          <TabPane tab="任务配置" key="configs">
            <Table
              columns={configColumns}
              dataSource={taskConfigs}
              rowKey="id"
              loading={configLoading}
              scroll={{ x: 1500 }}
              pagination={false}
            />
          </TabPane>

          <TabPane tab="执行日志" key="logs">
            <Space style={{ marginBottom: 16 }} wrap>
              <Select
                placeholder="任务类型"
                style={{ width: 150 }}
                allowClear
                value={filters.taskType}
                onChange={(value) => {
                  setFilters({ ...filters, taskType: value });
                  setPage(1);
                }}
              >
                {taskTypes.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>

              <Select
                placeholder="状态"
                style={{ width: 120 }}
                allowClear
                value={filters.status}
                onChange={(value) => {
                  setFilters({ ...filters, status: value });
                  setPage(1);
                }}
              >
                <Option value="running">执行中</Option>
                <Option value="success">成功</Option>
                <Option value="failed">失败</Option>
              </Select>

              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => {
                  setFilters({ ...filters, dateRange: dates });
                  setPage(1);
                }}
              />
            </Space>

            <Table
              columns={columns}
              dataSource={logs}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1200 }}
              pagination={{
                current: page,
                pageSize: limit,
                total,
                showSizeChanger: false,
                showTotal: (total) => `共 ${total} 条`,
                onChange: (page) => setPage(page)
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="任务执行详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="任务ID">{selectedLog.id}</Descriptions.Item>
              <Descriptions.Item label="任务名称">{selectedLog.task_name}</Descriptions.Item>
              <Descriptions.Item label="任务类型">
                {taskTypes.find(t => t.value === selectedLog.task_type)?.label}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={statusMap[selectedLog.status]?.color}>
                  {statusMap[selectedLog.status]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="开始时间">
                {dayjs(selectedLog.start_time).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="结束时间">
                {selectedLog.end_time ? dayjs(selectedLog.end_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="执行耗时">
                {selectedLog.duration ? `${Math.round(selectedLog.duration)}ms` : '-'}
              </Descriptions.Item>
            </Descriptions>

            {selectedLog.result_data && (
              <div style={{ marginTop: 16 }}>
                <h4>执行结果：</h4>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 4,
                  maxHeight: 300,
                  overflow: 'auto'
                }}>
                  {JSON.stringify(selectedLog.result_data, null, 2)}
                </pre>
              </div>
            )}

            {selectedLog.error_message && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ color: '#ff4d4f' }}>错误信息：</h4>
                <div style={{ 
                  background: '#fff2f0', 
                  padding: 12, 
                  borderRadius: 4,
                  color: '#ff4d4f'
                }}>
                  {selectedLog.error_message}
                </div>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* 统计弹窗 */}
      <Modal
        title="任务执行统计（最近7天）"
        open={statsVisible}
        onCancel={() => setStatsVisible(false)}
        footer={null}
        width={900}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {stats.map((stat) => (
            <Card key={stat.date} size="small">
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic 
                    title="日期" 
                    value={dayjs(stat.date).format('MM-DD')} 
                  />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="总执行次数" 
                    value={stat.total_count} 
                  />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="成功" 
                    value={stat.success_count}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="失败" 
                    value={stat.failed_count}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
              </Row>
              <div style={{ marginTop: 8 }}>
                平均耗时: {stat.avg_duration ? `${Math.round(stat.avg_duration)}ms` : '-'}
              </div>
            </Card>
          ))}
        </Space>
      </Modal>

      {/* 编辑任务弹窗 */}
      <Modal
        title="编辑任务配置"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleSaveTask}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="任务名称"
            name="task_name"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Cron表达式"
            name="cron_expression"
            rules={[{ required: true, message: '请输入Cron表达式' }]}
            extra="例如: 0 1 * * * (每天凌晨1点)"
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="任务描述"
            name="description"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CronTaskLogs;
